import styled, { keyframes, css } from 'styled-components';
import SendIcon from '@mui/icons-material/Send';  // Ensure you're importing the icon

export const ChatContainer = styled.div<{ $expanded: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg-main);
  transition: margin-left 0.3s ease;
  position: relative;
  overflow: hidden;
`;

export const RectangleButton = styled.button`
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
  border: none;
  padding: 8px 16px;
  margin-left: 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 24px rgba(37, 99, 235, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const ScheduleInterviewButton = styled(RectangleButton)`
  margin-left: 0; /* Remove left margin for table button */
  padding: 6px 12px; /* Adjust padding for better appearance */
  font-size: 12px;
`;

export const AppContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
`;

export const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  justify-content: ${(props) => (props.$isUser ? "flex-end" : "flex-start")};
  width: 100%;
  max-width: 860px;
`;

export const Avatar = styled.div<{ $isUser: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #1E1E2F; /* Always use dark background */
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  margin: ${(props) => (props.$isUser ? "0 0 0 8px" : "0 8px 0 0")};
  flex-shrink: 0;
  overflow: hidden; /* keeps image inside circle */
`;

export const AvatarImage = styled.img`
  width: 60%;  /* size inside circle */
  height: 60%;
  object-fit: contain;
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 0px rgba(255, 163, 37, 0.0); }
  50% { box-shadow: 0 0 16px rgba(255, 163, 37, 0.7); }
  100% { box-shadow: 0 0 0px rgba(255, 163, 37, 0.0); }
`;

export const GlowingAvatar = styled(Avatar)`
  animation: ${glowPulse} 1.2s ease-in-out infinite;
`;

export const BubbleWrapper = styled.div<{ $isUser: boolean }>`
  flex: 1;
  max-width: ${(props) => (props.$isUser ? "70%" : "100%")};
`;

export const MainContent = styled.div<{ $rightPanelWidth: number }>`
  flex: 1;
  display: flex;
  flex-direction: row;
  transition: all 0.3s ease;
  width: 100%;
`;

export const RightPanel = styled.div<{ width: number, $isCollapsed: boolean }>`
  width: ${props => props.$isCollapsed ? '0' : `${props.width}px`};
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  background-color: white;
  border-left: 1px solid var(--border-color);
  transition: width 0.3s ease;
  overflow-x: hidden;
  z-index: 5;
  display: flex;
  flex-direction: column;
`;

export const ResizeHandle = styled.div`
  width: 5px;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  cursor: col-resize;
  background-color: transparent;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &:active {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

export const RightPanelHeader = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-color); /* Fixed RU( typo */
`;

export const RightPanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

export const RightPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

export const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  &:hover {
    background-color: var(--hover-color);
  }
`;

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ActivityCard = styled.div<{ $type: string }>`
  padding: 12px;
  border-radius: 8px;
  background-color: var(--secondary-color);
  border-left: 44px solid ${props => {
    switch (props.$type) {
      case 'incident_created': return '#4caf50';
      case 'status_updated': return '#2196f3';
      case 'incident_resolved': return '#9c27b0';
      case 'info_retrieved': return '#ff9800';
      default: return '#757575';
    }
  }};
`;

export const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const ActivityTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
`;

export const ActivityTime = styled.span`
  font-size: 12px;
  color: #666;
`;

export const ActivityDescription = styled.p`
  margin: 4px 0;
  font-size: 13px;
  color: #333;
`;

export const ActivityDetails = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  font-size: 12px;
`;

export const Header = styled.div`
  height: 64px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: none;
  background: var(--bg-main);
`;

export const HeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--primary-color);
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

export const HeaderSearch = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 240px;
`;

export const HeaderSearchIcon = styled.span`
  position: absolute;
  left: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
`;

export const HeaderSearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 14px 0 36px;
  background: var(--surface-muted);
  border: none;
  border-radius: 999px;
  font-size: 13px;
  color: var(--primary-color);
  outline: none;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    background: var(--surface-color);
  }
`;

export const HeaderDivider = styled.div`
  width: 1px;
  height: 28px;
  background: #e2e8f0;
  margin: 0 6px;
`;

export const HeaderButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  &:hover {
    background-color: var(--hover-color);
  }
`;

export const MessageContainer = styled.div`
  flex: 1;
  padding: 32px 40px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
  background: var(--bg-main);
  contain: content;
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _StyledSendIcon = styled(SendIcon)`
  font-size: 50px;
`;

export const InputContainer = styled.div<{ $isEmpty?: boolean }>`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 0 24px;
  display: flex;
  justify-content: center;
  background: linear-gradient(to top, var(--bg-main) 70%, transparent 100%);
  z-index: 10;
  transform: translateZ(0);
  gap: 8px;
  position: relative;
  align-items: center;
  ${({ $isEmpty }) =>
    $isEmpty &&
    css`
      position: static;
      top: auto;
      left: auto;
      right: auto;
      transform: none;
      padding: 12px 0 4px;
      background: transparent;
      pointer-events: auto;
      margin-top: 4px;
      width: 100%;
    `}
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  background: var(--surface-color);
  border-radius: 28px;
  padding: 8px 12px 8px 20px;
  position: relative;
  width: min(980px, 100%);
  min-height: 56px;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3), 0 4px 20px rgba(0, 0, 0, 0.04);
  }
`;

export const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const PreviewContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0 4px 8px;
  position: relative;
  width: fit-content;
`;

export const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  object-fit: contain;
`;

export const PreviewRemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  color: var(--text-muted);
  font-size: 12px;
  &:hover {
    background: var(--hover-color);
    color: #ef4444;
  }
`;

export const AttachmentsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 8px;
  max-height: 100px;
  overflow-y: auto;
  width: 100%;
`;

export const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: var(--secondary-color);
  border-radius: 12px;
  font-size: 12px;
`;

export const AttachmentName = styled.span`
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: var(--accent-color);
  }
`;

export const AttachmentThumbnail = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-size: cover;
  background-position: center;
  background-color: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #666;
`;

export const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.2s ease;
  align-self: flex-end; /* Align with input */
  &:hover {
    background-color: var(--hover-color);
    transform: translateY(-1px);
  }
`;

export const AttachmentIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 20px;
    height: 20px;
    fill: var(--accent-color);
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FileName = styled.span`
  margin-left: 10px;
  font-size: 0.9rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px; /* Adjust as needed */
`;

export const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const TextArea = styled.textarea`
  flex: 1;
  padding: 12px 16px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  resize: none; /* Disable manual resize */
  min-height: 40px; /* Adjust as needed */
  max-height: 200px; /* Limit max height */
  overflow-y: auto; /* Enable scrolling when content exceeds max height */
  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const IconButton = styled.button`
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-muted);
  position: relative;
  &:hover {
    background-color: var(--hover-color);
  }
`;

export const WelcomeCard = styled.div`
  background: transparent;
  border-radius: 16px;
  padding: 0;
  border: none;
  box-shadow: none;
  max-width: 720px;
  margin: 24px auto 12px;
`;

export const WelcomeTitle = styled.h1`
  font-size: 26px;
  margin-bottom: 24px;
  text-align: center;
  font-weight: 600;
  line-height: 1.4;
  color: inherit;
`;

export const WelcomeSubtitle = styled.p`
  font-size: 14px;
  text-align: center;
  color: inherit;
  margin: 0;
  font-weight: 600;
`;

export const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 8px;
  width: 100%;
  max-width: 1200px;
  align-items: stretch;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const SuggestionCard = styled.div`
  background: var(--surface-color);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  border: 1px solid var(--border-color);
  border-top: 4px solid #3b82f6;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s;
  width: 100%;
  min-height: 120px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  }
`;

export const SuggestionBody = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const SuggestionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--hover-color);
  font-size: 24px;
`;

export const SuggestionContent = styled.div`
  flex: 1;
`;

export const SuggestionTitle = styled.h3`
  font-size: 15px;
  margin-bottom: 4px;
  font-weight: 700;
  color: var(--primary-color);
`;

export const SuggestionDescription = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
  margin: 0;
`;

export const SuggestionArrow = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #2563eb;
  margin-top: 8px;
  align-self: flex-end;
`;

export const waveAnimation = keyframes`
  0% { height: 5px; }
  20% { height: 15px; }
  40% { height: 10px; }
  60% { height: 20px; }
  80% { height: 10px; }
  100% { height: 5px; }
`;

export const LiveSpeechIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  z-index: 100;
  padding: 0 15px;
`;

export const WaveBar = styled.div<{ $delay: number }>`
  width: 3px;
  height: 5px;
  background-color: #f44336;
  border-radius: 1px;
  animation: ${waveAnimation} 1.2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

export const LiveIndicatorText = styled.span`
  color: white;
  font-size: 12px;
  margin-left: 8px;
`;

export const StatusBadge = styled.div<{ $status: 'loading' | 'error' | 'connected' }>`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 10px;
  background-color: ${props => {
    switch (props.$status) {
      case 'loading': return '#f0f0f0';
      case 'error': return '#ffebee';
      case 'connected': return '#e8f5e9';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'loading': return '#757575';
      case 'error': return '#d32f2f';
      case 'connected': return '#388e3c';
      default: return '#757575';
    }
  }};
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    background-color: ${props => {
    switch (props.$status) {
      case 'loading': return '#bdbdbd';
      case 'error': return '#f44336';
      case 'connected': return '#4caf50';
      default: return '#bdbdbd';
    } // Added missing closing brace here
  }};
  }
`;

// Add Spinner and LoadingText styles below

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  div {
    width: 6px;
    height: 6px;
    background-color: #555;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
  }

  div:nth-child(1) { animation-delay: 0s; }
  div:nth-child(2) { animation-delay: 0.2s; }
  div:nth-child(3) { animation-delay: 0.4s; }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;


export const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-top: 10px;
  background-color: #f0f0f0;
  border-radius: 8px;
  width: fit-content;
  align-self: center;
`;

export const LoadingText = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  padding: 4px 0;
`;

export const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: var(--primary-color);
  padding: 2px 0;
  border-radius: 0;
  max-width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const StatusBadgeWithRetry = styled(StatusBadge)`
  padding-right: 0;
  display: flex;
  align-items: center;
`;

export const RetryButton = styled.button`
  background: transparent;
  border: none;
  color: #2196f3;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  margin-left: 8px;
  border-radius: 4px;
  &:hover {
    background-color: rgba(33, 150, 243, 0.1);
  }
`;
export const TableWrapper = styled.div`
  margin-top: 8px;
  max-width: 100%;
  overflow-x: auto; /* Allow horizontal scroll for wide tables */
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: var(--surface-color);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const TableHeader = styled.th`
  background-color: var(--surface-muted);
  padding: 8px 10px;
  text-align: left;
  font-weight: 500;
  font-size: 12px;
  color: var(--primary-color);
  border-bottom: 1px solid var(--border-color);
`;

export const TableCell = styled.td`
  padding: 8px 10px;
  font-size: 12px;
  color: var(--primary-color);
  border-bottom: 1px solid var(--border-color);
`;

export const FormContainer = styled.div`
  background-color: var(--surface-color);
  padding: 16px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 12px;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;

  h4 {
    font-size: 16px;
    margin-bottom: 12px;
    color: var(--primary-color);
    text-align: center;
  }

  div {
    margin-bottom: 12px;
  }

  label {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
    color: var(--primary-color);
    font-size: 12px;
  }

  input[type="text"],
  input[type="date"],
  input[type="time"],
  select,
  textarea {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--border-color);
    background-color: var(--surface-color);
    border-radius: 5px;
    font-size: 13px;
    color: var(--primary-color);
    box-sizing: border-box; 

    &:focus {
      border-color: #FFA325;
      outline: none;
      box-shadow: 0 0 0 2px rgba(255, 163, 37, 0.2);
    }
  }

  textarea {
    min-height: 72px;
    resize: vertical;
  }

  /* Grid layout for dynamic rows */
  .grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 10px;
  }

  .grid-item {
    /* Each item will set grid-column: span N inline based on field span */
  }

  button[type="submit"] {
    background-color: #FFA325;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    width: 100%;

    &:hover {
      background-color: #e6951c;
    }
  }
`;

// Attachment preview styles used by AttachmentPreviewList
// AttachmentList removed (replaced by newer version at bottom)

export const AttachmentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: #fff;
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 10px;
`;

export const AttachmentThumb = styled.div<{ $isImage: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  background: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: ${(p) => (p.$isImage ? 'block' : 'none')};
  }
`;

export const AttachmentTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AttachmentMeta = styled.div`
  font-size: 12px;
  color: #666;
`;

export const AttachmentActions = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AttachmentActionButton = styled.button`
  background: #f5f7fa;
  border: 1px solid var(--border-color, #e0e0e0);
  color: #333;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: #eef1f5; }
`;

export const RemoveAttachmentButton = styled(AttachmentActionButton)`
  color: #b00020;
  border-color: #f1c3c8;
  &:hover { background: #fdecee; }
`;

export const AttachmentChildren = styled.ul`
  margin: 4px 0 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #555;
  max-height: 90px;
  overflow: auto;
`;

export const AttachmentChip = styled.div`
  display: flex;
  align-items: center;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 8px 12px;
  gap: 12px;
  width: fit-content;
  max-width: 300px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

export const ChipIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444; /* PDF red, or dynamic based on file type if needed */
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ChipContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ChipTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChipSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 500;
`;

export const ChipRemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-left: 4px;

  &:hover {
    background: #f3f4f6;
    color: #4b5563;
  }
`;

export const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
`;

/* ─── Chat Attachment Feature Styles ─── */

export const DragOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(59, 130, 246, 0.08);
  border: 2px dashed #3b82f6;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
  backdrop-filter: blur(2px);
`;

export const DragOverlayText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #3b82f6;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AttachmentPreviewStrip = styled.div`
  display: flex;
  gap: 10px;
  padding: 8px 4px;
  overflow-x: auto;
  width: 100%;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

export const AttachmentPreviewTile = styled.div<{ $isImage?: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: ${props => props.$isImage ? '80px' : 'auto'};
  min-width: ${props => props.$isImage ? '80px' : '140px'};
  max-width: 200px;
  height: ${props => props.$isImage ? '80px' : 'auto'};
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  background: #f8fafc;
  display: flex;
  align-items: center;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
`;

export const PreviewTileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const PreviewTileFile = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  width: 100%;
`;

export const PreviewFileIcon = styled.span`
  font-size: 20px;
  flex-shrink: 0;
  z-index: 1;
`;

export const PreviewFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

export const PreviewFileName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PreviewFileSize = styled.span`
  font-size: 10px;
  color: #94a3b8;
`;

export const PreviewTileRemove = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  color: #64748b;
  line-height: 1;
  padding: 0;
  z-index: 2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
    border-color: #fca5a5;
  }
`;

export const CircularLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  animation: _spinLoader 1s linear infinite;

  @keyframes _spinLoader {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const CircularProgressContainer = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
`;

export const ImageCircularProgressContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.8);
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 2;
`;


/* ─── In-Message Attachment Rendering ─── */

export const MessageAttachmentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

export const MessageImagePreview = styled.img`
  max-width: 280px;
  max-height: 240px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

export const FileCardStyled = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--surface-muted);
  text-decoration: none;
  color: var(--primary-color);
  min-width: 180px;
  max-width: 280px;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: #f1f5f9;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
`;

export const FileCardIcon = styled.span`
  font-size: 28px;
  flex-shrink: 0;
`;

export const FileCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
`;

export const FileCardName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--primary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileCardSize = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

export const FileCardDownload = styled.span`
  font-size: 11px;
  color: #3b82f6;
  font-weight: 500;
  flex-shrink: 0;
`;

export const ImageLightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: zoom-out;
  padding: 24px;
`;

export const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  object-fit: contain;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
`;
